import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { openControlPort, makeAck, makeStatus } from '@/events/controlChannel';
import { dispatchControlCommand } from '@/domain/controlDispatch';
import { buildControlRoster } from '@/domain/controlRoster';
import { simulationApi } from '@/events/simulationEngine';
import { useSimulationStore } from '@/state/simulationStore';

/** First writer wins: claim the role only if currently unclaimed. */
export function claimExecutor(current: string | null, myTabId: string): string {
  return current ?? myTabId;
}

/** A tab executes commands only when it holds the executor role. */
export function shouldExecute(executorId: string | null, myTabId: string): boolean {
  return executorId !== null && executorId === myTabId;
}

/** Deterministic election: the executor is the smallest tabId observed.
 *  A tab only yields to a strictly smaller id, so peers converge with no flip. */
export function resolveExecutor(current: string | null, observed: string, myTabId: string): string {
  const base = current ?? myTabId;
  return observed < base ? observed : base;
}

// A per-tab id. Counter-based (no Date.now/Math.random which are banned/flaky).
let tabCounter = 0;
function makeTabId(): string {
  tabCounter += 1;
  return `tab-${tabCounter}-${performance.now().toFixed(0)}`;
}

/**
 * Mounted ONLY in AppShell so it never runs in the console tab. Receives control
 * commands, executes them when this tab holds the executor role, and broadcasts
 * status so the console can show connection + live clock. Also handles rebind:
 * a status message naming a different executor yields the role.
 */
export function useControlBridge(): void {
  const navigate = useNavigate();
  const clockIso = useSimulationStore((s) => s.clockIso);
  const tabIdRef = useRef<string>('');
  const executorRef = useRef<string | null>(null);
  const portRef = useRef<ReturnType<typeof openControlPort> | null>(null);
  if (!tabIdRef.current) tabIdRef.current = makeTabId();

  const broadcastStatus = (port: ReturnType<typeof openControlPort>) => {
    const sim = useSimulationStore.getState();
    port.post(
      makeStatus({
        clockIso: sim.clockIso,
        firedEventIds: simulationApi.fired(),
        counts: { fired: simulationApi.fired().length, remaining: simulationApi.remaining() },
        executorId: executorRef.current ?? tabIdRef.current,
        ts: sim.clockIso,
        roster: buildControlRoster(),
      }),
    );
  };

  useEffect(() => {
    const port = openControlPort();
    portRef.current = port;
    const myTabId = tabIdRef.current;
    // Claim executor on mount (first tab wins; helper keeps an existing claim).
    executorRef.current = claimExecutor(executorRef.current, myTabId);

    const unsub = port.subscribe((msg) => {
      if (msg.kind === 'hello') {
        broadcastStatus(port);
        return;
      }
      if (msg.kind === 'status') {
        // Deterministic election: converge on the smallest tabId observed.
        if (msg.executorId) {
          const next = resolveExecutor(executorRef.current, msg.executorId, myTabId);
          if (next !== executorRef.current) {
            executorRef.current = next;
            broadcastStatus(port); // re-announce so the new minimum gossips onward
          }
        }
        return;
      }
      if (msg.kind === 'ack') return; // our own echo
      // command
      if (!shouldExecute(executorRef.current, myTabId)) return;
      if (msg.navigateTo) navigate(msg.navigateTo);
      const result = dispatchControlCommand(msg.action, msg.payload);
      port.post(
        makeAck({
          commandId: msg.id,
          ok: result.ok,
          note: result.note,
          ts: useSimulationStore.getState().clockIso,
        }),
      );
      broadcastStatus(port);
    });

    broadcastStatus(port); // announce on mount
    return () => {
      unsub();
      port.close();
      portRef.current = null;
    };
  }, [navigate]);

  // Re-broadcast whenever the clock changes so the console clock stays live.
  useEffect(() => {
    if (portRef.current) broadcastStatus(portRef.current);
  }, [clockIso]);
}
